import { useMemo } from "react";
import { AlertCircle, AlertTriangle, Info, Check } from "lucide-react";
import { getPendingSceneObservations, getSceneSeverity } from "@/domain/continuity";
import { t } from "@/i18n";

/**
 * Indicador discreto de continuidad cinematográfica para tarjetas de escena.
 * Muestra el número de observaciones de raccord pendientes y su severidad dominante.
 */
export const ContinuityIndicator = ({
  sceneId,
  observations = [],
  decisions = {},
  onClick,
  showWhenEmpty = false,
  className = "",
}) => {
  const pending = useMemo(
    () => getPendingSceneObservations(sceneId, observations, decisions).filter((o) => o.severity !== "info"),
    [sceneId, observations, decisions]
  );

  const severity = useMemo(() => getSceneSeverity(pending), [pending]);

  if (pending.length === 0) {
    if (!showWhenEmpty) return null;
    return (
      <span
        data-testid={`scene-continuity-clean-${sceneId}`}
        className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-mono text-[10px] text-[#6b6b6b] ${className}`}
        title="Raccord verificado (sin avisos pendientes)"
      >
        <Check size={11} className="text-[#a3a3a3]" />
      </span>
    );
  }

  const badgeStyles = {
    conflict: "border-red-500/40 bg-red-500/10 text-red-400 hover:border-red-500/60",
    warning: "border-amber-500/40 bg-amber-500/10 text-amber-400 hover:border-amber-500/60",
    info: "border-[#3a3a3a] bg-[#1f1f1f] text-[#a3a3a3] hover:text-[#f4f1ec]",
  };

  const Icon = severity === "conflict" ? AlertCircle : (severity === "warning" ? AlertTriangle : Info);

  return (
    <button
      type="button"
      data-testid={`scene-continuity-indicator-${sceneId}`}
      onClick={(e) => {
        if (onClick) {
          e.stopPropagation();
          onClick(sceneId);
        }
      }}
      className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 font-mono text-[10px] font-medium transition-all ${
        badgeStyles[severity] || badgeStyles.info
      } ${className}`}
      title={`${pending.length} aviso${pending.length === 1 ? "" : "s"} de raccord`}
      aria-label={`${pending.length} aviso${pending.length === 1 ? "" : "s"} de raccord`}
    >
      <Icon size={11} className="shrink-0" />
      <span>{pending.length}</span>
    </button>
  );
};

export default ContinuityIndicator;
